'use client';

import type { GA4Item } from '@lib/analytics/types';
import { fetchMoreProducts, listProductsWithSort } from '@lib/data/products';
import { isPriceFiltered } from '@lib/util/filter-products';
import type { SortOptions } from '@lib/util/sort-products';
import { toPriceOrder } from '@lib/util/sort-products';
import { toListingVMs } from '@lib/util/to-listing-vms';
import {
  buildProductQueryParams,
  PLP_FIELDS,
} from '@modules/store/lib/product-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProductCardVM } from 'types/view-models';

const PAGE_SIZE = 12;
// Price-filtered (non-explicit-sort) listings fetch everything up front and
// render it in one grid — mirrors `PriceFilteredProducts`, no further
// pagination beyond this ceiling.
const PRICE_FILTER_LIMIT = 100;

export type UseProductListingParams = {
  sortBy: SortOptions;
  minPrice?: number;
  maxPrice?: number;
  countryCode: string;
  categoryId?: string;
  collectionId?: string;
  searchQuery?: string;
  listId?: string;
  listName?: string;
  /** SSR-rendered default view (`popular`, no price filter) — reused as-is
   * when the resolved params match the default, so the common case never
   * triggers an extra client fetch. */
  defaultProducts: ProductCardVM[];
  defaultGa4Items: GA4Item[];
  defaultCount: number;
};

type ListingPage = {
  cards: ProductCardVM[];
  ga4Items: GA4Item[];
  count: number;
  hasMore: boolean;
};

// "Most Popular" ranks via the best-seller endpoint, which only supports
// category (or global) scoping — collection/search listings fall back to the
// standard endpoint even for the default sort. Mirrors `isPopularSorted` in
// `src/modules/store/templates/paginated-products.tsx`.
function usesPopularEndpoint(
  sortBy: SortOptions,
  options: { searchQuery?: string; collectionId?: string },
): boolean {
  return sortBy === 'popular' && !options.searchQuery && !options.collectionId;
}

function resolveListingStrategy(
  sortBy: SortOptions,
  minPrice: number | undefined,
  maxPrice: number | undefined,
  options: { searchQuery?: string; collectionId?: string },
) {
  const priceFiltered = isPriceFiltered(minPrice, maxPrice);
  const explicitPriceSort = sortBy === 'price_asc' || sortBy === 'price_desc';

  return {
    // Price filter without an explicit price sort fetches everything
    // matching up front (no further pagination) — mirrors `PriceFilteredProducts`.
    isPriceFilterOnly: priceFiltered && !explicitPriceSort,
    isDefault: sortBy === 'popular' && !priceFiltered,
    priceOrder: explicitPriceSort ? toPriceOrder(sortBy) : undefined,
    popularOrder: usesPopularEndpoint(sortBy, options)
      ? ('desc' as const)
      : undefined,
  };
}

// Fetches everything matching a price filter (no explicit sort) in one shot
// — mirrors `PriceFilteredProducts`, no further pagination.
async function fetchPriceFilteredPage(
  params: Pick<
    UseProductListingParams,
    | 'sortBy'
    | 'minPrice'
    | 'maxPrice'
    | 'countryCode'
    | 'categoryId'
    | 'collectionId'
    | 'searchQuery'
    | 'listId'
    | 'listName'
  >,
): Promise<ListingPage> {
  const result = await listProductsWithSort({
    page: 1,
    queryParams: {
      ...buildProductQueryParams({
        categoryId: params.categoryId,
        collectionId: params.collectionId,
        searchQuery: params.searchQuery,
        sort: params.sortBy,
        limit: PRICE_FILTER_LIMIT,
      }),
      fields: PLP_FIELDS,
    },
    sortBy: params.sortBy,
    countryCode: params.countryCode,
    minPrice: params.minPrice,
    maxPrice: params.maxPrice,
  });

  const { cards, ga4Items } = toListingVMs(result.response.products, {
    listId: params.listId,
    listName: params.listName,
  });

  return { cards, ga4Items, count: result.response.count, hasMore: false };
}

// Fetches one page (offset 0 for the initial override load, or a running
// offset for "load more") via `fetchMoreProducts` — already handles
// best-seller/price-sorted/standard listings identically to the SSR templates.
async function fetchListingPage(
  params: Pick<
    UseProductListingParams,
    | 'sortBy'
    | 'minPrice'
    | 'maxPrice'
    | 'countryCode'
    | 'categoryId'
    | 'collectionId'
    | 'searchQuery'
    | 'listId'
    | 'listName'
  > & {
    offset: number;
    priceOrder?: 'asc' | 'desc';
    popularOrder?: 'asc' | 'desc';
  },
): Promise<ListingPage> {
  const result = await fetchMoreProducts({
    offset: params.offset,
    limit: PAGE_SIZE,
    countryCode: params.countryCode,
    sortBy: params.sortBy,
    categoryId: params.categoryId,
    collectionId: params.collectionId,
    searchQuery: params.searchQuery,
    priceOrder: params.priceOrder,
    popularOrder: params.popularOrder,
    minPrice: params.minPrice,
    maxPrice: params.maxPrice,
  });

  const { cards, ga4Items } = toListingVMs(result.products, {
    listId: params.listId,
    listName: params.listName,
  });

  return { cards, ga4Items, count: result.count, hasMore: result.hasMore };
}

export function useProductListing({
  sortBy,
  minPrice,
  maxPrice,
  countryCode,
  categoryId,
  collectionId,
  searchQuery,
  listId,
  listName,
  defaultProducts,
  defaultGa4Items,
  defaultCount,
}: UseProductListingParams) {
  const { isDefault, isPriceFilterOnly, priceOrder, popularOrder } =
    resolveListingStrategy(sortBy, minPrice, maxPrice, {
      searchQuery,
      collectionId,
    });

  const [products, setProducts] = useState<ProductCardVM[]>(
    isDefault ? defaultProducts : [],
  );
  const [ga4Items, setGa4Items] = useState<GA4Item[]>(
    isDefault ? defaultGa4Items : [],
  );
  const [count, setCount] = useState(isDefault ? defaultCount : 0);
  const [isOverriding, setIsOverriding] = useState(!isDefault);
  const [hasMore, setHasMore] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const loadingRef = useRef(false);
  const queryVersionRef = useRef(0);
  // Tracks the next server-side offset to request. Ranked (popularity/price)
  // listings hydrate IDs into products and drop any that no longer resolve,
  // so `products.length` can undercount a page that requested PAGE_SIZE —
  // using it as the next offset would re-request already-seen IDs. The
  // server's `count`/`hasMore` are computed off the requested offset+limit
  // window, so the running offset must advance by PAGE_SIZE regardless of
  // how many items actually hydrated.
  const nextOffsetRef = useRef(0);

  const paramsKey = JSON.stringify({
    sortBy,
    minPrice,
    maxPrice,
    categoryId,
    collectionId,
    searchQuery,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey is the serialised query identity for this effect; defaultProducts/defaultCount are only read for the default branch
  useEffect(() => {
    queryVersionRef.current += 1;
    const version = queryVersionRef.current;
    loadingRef.current = false;
    setIsPending(false);

    if (isDefault) {
      setProducts(defaultProducts);
      setGa4Items(defaultGa4Items);
      setCount(defaultCount);
      setIsOverriding(false);
      setHasMore(defaultCount > defaultProducts.length);
      nextOffsetRef.current = PAGE_SIZE;
      return;
    }

    setProducts([]);
    setGa4Items([]);
    setCount(0);
    setIsOverriding(true);
    setHasMore(false);
    nextOffsetRef.current = 0;

    const page = isPriceFilterOnly
      ? fetchPriceFilteredPage({
          sortBy,
          minPrice,
          maxPrice,
          countryCode,
          categoryId,
          collectionId,
          searchQuery,
          listId,
          listName,
        })
      : fetchListingPage({
          offset: 0,
          sortBy,
          minPrice,
          maxPrice,
          countryCode,
          categoryId,
          collectionId,
          searchQuery,
          listId,
          listName,
          priceOrder,
          popularOrder,
        });

    page
      .then((result) => {
        if (queryVersionRef.current !== version) return;
        setProducts(result.cards);
        setGa4Items(result.ga4Items);
        setCount(result.count);
        setIsOverriding(false);
        setHasMore(result.hasMore);
        nextOffsetRef.current = PAGE_SIZE;
      })
      .catch(() => {
        if (queryVersionRef.current !== version) return;
        setIsOverriding(false);
      });
  }, [paramsKey, isDefault, isPriceFilterOnly]);

  const loadMore = useCallback(() => {
    if (
      loadingRef.current ||
      isPending ||
      isOverriding ||
      isPriceFilterOnly ||
      !hasMore
    ) {
      return;
    }
    loadingRef.current = true;
    setIsPending(true);
    const version = queryVersionRef.current;
    const offset = nextOffsetRef.current;
    fetchListingPage({
      offset,
      sortBy,
      minPrice,
      maxPrice,
      countryCode,
      categoryId,
      collectionId,
      searchQuery,
      listId,
      listName,
      priceOrder,
      popularOrder,
    })
      .then((result) => {
        if (queryVersionRef.current !== version) return;
        setProducts((prev) => [...prev, ...result.cards]);
        setGa4Items((prev) => [...prev, ...result.ga4Items]);
        setHasMore(result.hasMore);
        nextOffsetRef.current = offset + PAGE_SIZE;
      })
      .catch(() => {})
      .finally(() => {
        if (queryVersionRef.current !== version) return;
        loadingRef.current = false;
        setIsPending(false);
      });
  }, [
    isPending,
    isOverriding,
    isPriceFilterOnly,
    hasMore,
    countryCode,
    sortBy,
    categoryId,
    collectionId,
    searchQuery,
    listId,
    listName,
    priceOrder,
    popularOrder,
    minPrice,
    maxPrice,
  ]);

  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  return {
    products,
    ga4Items,
    count,
    isOverriding,
    hasMore,
    isPending,
    loadMoreRef,
  };
}
